import { useContextMenuMESF } from "@dexteel/mesf-core";
import {
	Backdrop,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import type {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowDragEndEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { EventEditorModal } from "@/pages/calendar/components/EventEditorModal";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import type { WorkOrder } from "@/shared/models/WorkOrder";
import type { AreaIds } from "../config/assetFieldConfig";
import { useNewWorkOrderWindow } from "../hooks/useNewWorkOrderWindow";
import { useTableData } from "../hooks/useTableData";
import { DeleteWorkOrderModal } from "./DeleteWorkOrderModal";
import { WorkOrderTimingModal } from "./WorkOrderTimingModal";

type Props = {
	isLoading: boolean;
	rows: WorkOrder[];
	assetId: number;
	parentAssetId: number | null;
	/** viewed shift window — the new work order's plan defaults inside it */
	shiftStart: Date | null;
	shiftEnd: Date | null;
	areaIds: AreaIds;
	isCurrentShift: boolean;
	isPeriod: boolean;
	/** Deep-link target (?workOrderId=): scrolled to and selected once. */
	scrollToWorkOrderId?: number | null;
	/** Reports the selected row so the page can mirror it in the URL. */
	onSelectedWorkOrderChange?: (workOrderId: number | null) => void;
	onReorder: (orderedIds: number[]) => void;
	onReorderCancel?: () => void;
	onNavigateToShift?: (shiftId: number) => void;
	onWorkOrderSaved: (workOrderNumber: string) => void;
};

export const WorkOrdersTable = ({
	isLoading,
	rows,
	assetId,
	parentAssetId,
	shiftStart,
	shiftEnd,
	areaIds,
	isCurrentShift,
	isPeriod,
	scrollToWorkOrderId,
	onSelectedWorkOrderChange,
	onReorder,
	onReorderCancel,
	onNavigateToShift,
	onWorkOrderSaved,
}: Props) => {
	const [swapConfirm, setSwapConfirm] = useState<number[] | null>(null);
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | "times" | ""
	>("");
	const [selectedRowData, setSelectedRowData] = useState<WorkOrder | null>(
		null,
	);
	const gridApiRef = useRef<GridApi<WorkOrder> | null>(null);

	const [gridReady, setGridReady] = useState(false);
	const scrolledForRef = useRef<number | null>(null);
	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { columnDefs, getMenuOptions } = useTableData({
		assetId,
		parentAssetId,
		areaIds,
		isCurrentShift,
		isPeriod,
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const onRowDoubleClicked = (event: any) => {
		setSelectedRowData(event.data);
		setShowModal("update");
	};

	// Always derive fresh data from rows to avoid stale state
	const currentWorkOrder = useMemo(() => {
		if (!selectedRowData) return null;
		return (
			rows.find((r) => r.WorkOrderId === selectedRowData.WorkOrderId) ??
			selectedRowData
		);
	}, [selectedRowData, rows]);

	// Both entries go through the calendar's editor so a work order always has a
	// plan window: creating opens on the first free stretch of the viewed shift,
	// editing carries the row's existing event. Fetched only while one is opening.
	const isCreating = showModal === "create";
	const isEditing = showModal === "update";
	const {
		window: newWindow,
		lineEvents,
		isLoading: isWindowLoading,
	} = useNewWorkOrderWindow({
		assetId,
		shiftStart,
		shiftEnd,
		anchorDate: isEditing ? currentWorkOrder?.ProductionDate : null,
		enabled: isCreating || isEditing,
	});

	const editorProps = useMemo(() => {
		if (isCreating)
			return newWindow
				? {
						defaultStart: newWindow.start,
						defaultEnd: newWindow.end,
						event: null,
						workOrder: null,
					}
				: null;
		if (!isEditing || !currentWorkOrder) return null;
		return {
			defaultStart: null,
			defaultEnd: null,
			event:
				lineEvents.find(
					(ev) => ev.WorkOrderId === currentWorkOrder.WorkOrderId,
				) ?? null,
			workOrder: currentWorkOrder,
		};
	}, [isCreating, isEditing, newWindow, lineEvents, currentWorkOrder]);

	const handleModalHide = useCallback(
		(shouldUpdate?: boolean, targetShiftId?: number | null) => {
			setShowModal("");
			if (shouldUpdate) {
				setSelectedRowData(null);
				if (!isPeriod && targetShiftId && onNavigateToShift) {
					onNavigateToShift(targetShiftId);
				}
			}
		},
		[onNavigateToShift, isPeriod],
	);

	const onRowDragEnd = useCallback(
		(event: RowDragEndEvent) => {
			if (!event.api) return;
			const orderedIds: number[] = [];
			event.api.forEachNodeAfterFilterAndSort((node) => {
				if (node.data?.WorkOrderId) {
					orderedIds.push(node.data.WorkOrderId);
				}
			});
			// Preempting swap: a Pending now sits ahead of the (not-yet-running) Loaded WO.
			// Confirm before demoting it, since it replaces what the operator sees on the HMI.
			const loaded = rows.find((r) => r.Status === "Loaded");
			if (loaded?.WorkOrderId) {
				const loadedIdx = orderedIds.indexOf(loaded.WorkOrderId);
				const pendingIds = new Set(
					rows.filter((r) => r.Status === "Pending").map((r) => r.WorkOrderId),
				);
				const preempts =
					loadedIdx > 0 &&
					orderedIds.slice(0, loadedIdx).some((id) => pendingIds.has(id));
				if (preempts) {
					setSwapConfirm(orderedIds);
					return;
				}
			}
			onReorder(orderedIds);
		},
		[onReorder, rows],
	);

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "workOrdersContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[showContextMenu],
	);

	useEffect(() => {
		registerConfig({
			id: "workOrdersContext",
			getOptions: getMenuOptions,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// One-shot: scroll to + select the deep-linked work order once the grid and
	// its rows are ready. Guarded per id so background refetches don't move the
	// scroll under the user.
	useEffect(() => {
		if (scrollToWorkOrderId == null) return;
		if (scrolledForRef.current === scrollToWorkOrderId) return;
		if (!gridReady || isLoading || rows.length === 0) return;
		const api = gridApiRef.current;
		const node = api?.getRowNode(String(scrollToWorkOrderId));
		// Missing means the filters hide it; the page clears them and this retries.
		if (!api || !node) return;
		api.ensureNodeVisible(node, "middle");
		node.setSelected(true, true);
		scrolledForRef.current = scrollToWorkOrderId;
	}, [rows, isLoading, gridReady, scrollToWorkOrderId]);

	const onSelectionChanged = useCallback(() => {
		// Early empty events while rows load would blank the URL param.
		if (isLoading) return;
		const selected = gridApiRef.current?.getSelectedRows() ?? [];
		onSelectedWorkOrderChange?.(selected[0]?.WorkOrderId ?? null);
	}, [isLoading, onSelectedWorkOrderChange]);

	return (
		<>
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				sx={{
					height: "68vh",
					minHeight: "68vh",
				}}
				spacing={1}
				component={Paper}
				elevation={2}
			>
				<Grid2 size={{ md: 12, xs: 12 }} height={"100%"}>
					<AgGridReact<WorkOrder>
						loading={isLoading}
						rowData={rows}
						columnDefs={columnDefs}
						animateRows
						rowDragManaged={!isPeriod}
						onRowDragEnd={isPeriod ? undefined : onRowDragEnd}
						getContextMenuItems={getContextMenuItems}
						onGridReady={(params) => {
							gridApiRef.current = params.api;
							setGridReady(true);
						}}
						onSelectionChanged={onSelectionChanged}
						onRowDoubleClicked={onRowDoubleClicked}
						loadingOverlayComponent={CenteredLazyLoading}
						getRowId={(params) => `${params.data.WorkOrderId}`}
						gridOptions={{
							rowSelection: {
								mode: "singleRow",
								checkboxes: false,
								enableClickSelection: true,
							},
						}}
					/>
				</Grid2>
			</Grid2>
			<FooterButton
				label="WORK ORDER"
				showCreateModal={showModal === "create"}
				setShowCreateModal={(show) =>
					show ? setShowModal("create") : setShowModal("")
				}
			/>
			<DeleteWorkOrderModal
				workOrder={currentWorkOrder}
				open={showModal === "delete"}
				onHide={handleModalHide}
			/>
			{editorProps && !isWindowLoading && (
				<EventEditorModal
					open
					workOrdersOnly
					assetId={assetId}
					areaIds={areaIds}
					lineEvents={lineEvents}
					{...editorProps}
					onHide={(success, _refs, workOrderNumber) => {
						setShowModal("");
						if (success) {
							setSelectedRowData(null);
							if (workOrderNumber) onWorkOrderSaved(workOrderNumber);
						}
					}}
				/>
			)}
			<Backdrop
				open={(isCreating || isEditing) && (!editorProps || isWindowLoading)}
				sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
			<WorkOrderTimingModal
				open={showModal === "times"}
				workOrderId={currentWorkOrder?.WorkOrderId ?? null}
				assetId={assetId}
				onClose={() => setShowModal("")}
			/>
			<Dialog
				open={swapConfirm !== null}
				onClose={() => {
					setSwapConfirm(null);
					onReorderCancel?.();
				}}
			>
				<DialogTitle>Replace loaded work order?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						This moves a pending work order ahead of the one currently loaded on
						the line. The loaded one goes back to Pending and the new top work
						order is sent to the HMI. Continue?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setSwapConfirm(null);
							onReorderCancel?.();
						}}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={() => {
							if (swapConfirm) onReorder(swapConfirm);
							setSwapConfirm(null);
						}}
					>
						Replace
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
